import { SignalWatcher } from "@lit-labs/signals";
import type { AnyRouter } from "@tanstack/router-core";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { app, initApp, queryClient } from "../init";
import { type HostHistory, createHostHistory } from "../lib/tsr/host-history";
import "../lib/tsr/outlet";
import { startRouter } from "../lib/tsr/router";
import { type AppRouter, createViewRouter } from "../routes/app-router";
import { mixins, shared } from "../styles";
import { ERROR_EVENT, type PentacodeErrorDetail, READY_EVENT, ROUTE_CHANGE_EVENT } from "./contract";

const NOT_AUTHENTICATED =
    "Du bist noch nicht bei Pentacode angemeldet. Sobald du angemeldet bist, werden die Daten hier automatisch angezeigt.";
const ROUTE_UNAVAILABLE = "Diese Seite steht dir als eigenständige Ansicht noch nicht zur Verfügung.";

/**
 * One pentacode page without the app around it (no menus, no header), e.g.
 * `<pentacode-view route="employees/all/time">`. Any number of views can sit on
 * a host page, next to a `<pentacode-app>` or on their own; they share the
 * app session and one query cache, each has its own router whose location the
 * host owns through `route`.
 */
export class PentacodeView extends SignalWatcher(LitElement) {
    @property({ reflect: true })
    route = "";

    @state()
    private _router?: AppRouter;

    @state()
    private _status: "booting" | "not-authenticated" | "ready" = "booting";

    private _history?: HostHistory;
    private _stopRouter?: () => void;
    private _reflecting = false;
    private _readyFired = false;

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
                min-height: 10em;
            }

            ptc-route-outlet {
                ${mixins.fullbleed()};
            }
        `,
    ];

    connectedCallback() {
        super.connectedCallback();
        void this._boot();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._teardown();
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("route") && !this._reflecting) {
            this._history?.setRoute(this.route);
        }
    }

    private async _boot() {
        if (!app) {
            initApp({ mode: "embedded", onRestartRequest: () => this._teardown() });
        }
        await app.loaded;
        if (!this.isConnected || this._router) {
            return;
        }
        if (!app.state.session) {
            this._status = "not-authenticated";
            this._dispatchError(NOT_AUTHENTICATED, "not-authenticated");
            app.subscribe(this._onAppState);
            return;
        }
        this._start();
    }

    private _onAppState = () => {
        if (app?.state.session && !this._router && this.isConnected) {
            app.unsubscribe(this._onAppState);
            this._start();
        }
    };

    private _start() {
        this._history = createHostHistory(this.route, (route, replace) => this._reflectRoute(route, replace));
        const router = createViewRouter(this._history, {
            app,
            queryClient,
            reportDenied: (path) => this._dispatchError(`No access to route: ${path}`, "route-denied"),
        });
        const stopRouter = startRouter(router);
        const stopResolved = router.subscribe("onResolved", () => {
            if (this._isUnavailable(router)) {
                this._dispatchError(ROUTE_UNAVAILABLE, "route-unavailable");
            } else if (!this._readyFired) {
                this._readyFired = true;
                this.dispatchEvent(new CustomEvent(READY_EVENT, { bubbles: true, composed: true }));
            }
        });
        this._stopRouter = () => {
            stopRouter();
            stopResolved();
            this._history?.destroy();
        };
        this._router = router;
        this._status = "ready";
    }

    private _teardown() {
        app?.unsubscribe(this._onAppState);
        this._stopRouter?.();
        this._stopRouter = undefined;
        this._router = undefined;
        this._history = undefined;
        this._status = "booting";
    }

    /** Only routes with a view render standalone: not the legacy catch-all, nor data-only routes like `roster`. */
    private _isUnavailable(router: AppRouter) {
        const leaf = router.stores.matches.get().at(-1);
        return !leaf || !(router as AnyRouter).routesById[leaf.routeId]?.options.component;
    }

    private _reflectRoute(route: string, replace: boolean) {
        this._reflecting = true;
        this.route = route;
        void this.updateComplete.then(() => (this._reflecting = false));
        this.dispatchEvent(
            new CustomEvent(ROUTE_CHANGE_EVENT, { detail: { route, replace }, bubbles: true, composed: true })
        );
    }

    private _dispatchError(message: string, code: PentacodeErrorDetail["code"]) {
        const detail: PentacodeErrorDetail = { message, code };
        this.dispatchEvent(new CustomEvent(ERROR_EVENT, { detail, bubbles: true, composed: true }));
    }

    render() {
        if (this._status === "not-authenticated") {
            return html`<div class="fullbleed centering padded layout subtle">${NOT_AUTHENTICATED}</div>`;
        }
        if (!this._router) {
            return nothing;
        }
        if (this._isUnavailable(this._router)) {
            return html`<div class="fullbleed centering padded layout subtle">${ROUTE_UNAVAILABLE}</div>`;
        }
        return html`<ptc-route-outlet .router=${this._router}></ptc-route-outlet>`;
    }
}
